import { useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import { getMyProfile, updateMyProfile, changePassword, uploadAvatar } from '../api/users';
import { User } from '@tinyrewards/shared';

export default function MyAccountPage() {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // Profile form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [profileSaving, setProfileSaving] = useState(false);

    // Password form state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [passwordSaving, setPasswordSaving] = useState(false);

    // Avatar state
    const [avatarMsg, setAvatarMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [avatarSaving, setAvatarSaving] = useState(false);

    // Public profile visibility
    const [publicPageShare, setPublicPageShare] = useState(false);
    const [publicMsg, setPublicMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [publicSaving, setPublicSaving] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        getMyProfile()
            .then(res => {
                if (res.success && res.data) {
                    const u = res.data;
                    setUser(u);
                    setFirstName(u.firstName);
                    setLastName(u.lastName);
                    setOrganizationName('');
                    setPublicPageShare(u.publicPageShare ?? false);
                }
            })
            .finally(() => setLoading(false));
    }, []);

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setProfileSaving(true);
        setProfileMsg(null);
        try {
            const res = await updateMyProfile({ firstName, lastName, organizationName: organizationName || undefined });
            if (res.success && res.data) {
                setUser(res.data);
                setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
            } else {
                setProfileMsg({ type: 'error', text: res.message ?? 'Failed to update profile.' });
            }
        } catch {
            setProfileMsg({ type: 'error', text: 'An unexpected error occurred.' });
        } finally {
            setProfileSaving(false);
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordMsg(null);
        if (newPassword !== confirmPassword) {
            setPasswordMsg({ type: 'error', text: 'New passwords do not match.' });
            return;
        }
        setPasswordSaving(true);
        try {
            const res = await changePassword(currentPassword, newPassword);
            if (res.success) {
                setPasswordMsg({ type: 'success', text: 'Password changed successfully.' });
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setPasswordMsg({ type: 'error', text: res.message ?? 'Failed to change password.' });
            }
        } catch {
            setPasswordMsg({ type: 'error', text: 'An unexpected error occurred.' });
        } finally {
            setPasswordSaving(false);
        }
    };

    const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAvatarSaving(true);
        setAvatarMsg(null);
        try {
            const res = await uploadAvatar(file);
            if (res.success && res.data) {
                setUser(prev => prev ? { ...prev, profileImage: res.data!.imageUrl } : prev);
                setAvatarMsg({ type: 'success', text: 'Avatar updated successfully.' });
            } else {
                setAvatarMsg({ type: 'error', text: res.message ?? 'Failed to upload avatar.' });
            }
        } catch {
            setAvatarMsg({ type: 'error', text: 'An unexpected error occurred.' });
        } finally {
            setAvatarSaving(false);
        }
    };

    const handleTogglePublic = async (next: boolean) => {
        setPublicPageShare(next); // optimistic
        setPublicSaving(true);
        setPublicMsg(null);
        try {
            const res = await updateMyProfile({ firstName, lastName, publicPageShare: next });
            if (res.success && res.data) {
                setUser(res.data);
                setPublicMsg({ type: 'success', text: next ? 'Your profile is now public.' : 'Your profile is now private.' });
            } else {
                setPublicPageShare(!next); // revert
                setPublicMsg({ type: 'error', text: res.message ?? 'Failed to update visibility.' });
            }
        } catch {
            setPublicPageShare(!next);
            setPublicMsg({ type: 'error', text: 'An unexpected error occurred.' });
        } finally {
            setPublicSaving(false);
        }
    };

    const publicUrl = user ? `${window.location.origin}/u/${user.userID}` : '';
    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard unavailable — no-op */
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Typography variant="h4" fontWeight={700} mb={3}>My Account</Typography>

            {/* Avatar Section */}
            <Card sx={{ mb: 3 }}>
                <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountCircleIcon color="action" fontSize="small" />
                    <Typography fontWeight={600}>Profile Photo</Typography>
                </Box>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={3}>
                        <Avatar
                            src={user?.profileImage}
                            sx={{ width: 80, height: 80, bgcolor: 'grey.400' }}
                        >
                            <AccountCircleIcon sx={{ fontSize: 48 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="body2" fontWeight={600} mb={0.5}>Upload new photo</Typography>
                            <Button component="label" disabled={avatarSaving}>
                                Choose file
                                <input type="file" accept="image/*" hidden onChange={handleAvatarChange} />
                            </Button>
                            {avatarMsg && <Alert severity={avatarMsg.type} sx={{ mt: 1, py: 0 }}>{avatarMsg.text}</Alert>}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Profile Section */}
            <Card sx={{ mb: 3 }}>
                <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon color="action" fontSize="small" />
                    <Typography fontWeight={600}>Edit Profile</Typography>
                </Box>
                <CardContent>
                    {profileMsg && <Alert severity={profileMsg.type} sx={{ mb: 2 }}>{profileMsg.text}</Alert>}
                    <Box component="form" onSubmit={handleProfileSubmit} display="flex" flexDirection="column" gap={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    id="firstName"
                                    label="First Name"
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    required
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    id="lastName"
                                    label="Last Name"
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    required
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <TextField
                            id="organizationName"
                            label="Organization Name"
                            value={organizationName}
                            onChange={e => setOrganizationName(e.target.value)}
                            size="small"
                            fullWidth
                        />
                        <Box>
                            <Button type="submit" variant="contained" disabled={profileSaving}>
                                {profileSaving ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                                Save Changes
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Public Profile Section */}
            <Card sx={{ mb: 3 }}>
                <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublicIcon color="action" fontSize="small" />
                    <Typography fontWeight={600}>Public Profile</Typography>
                </Box>
                <CardContent>
                    {publicMsg && <Alert severity={publicMsg.type} sx={{ mb: 2 }}>{publicMsg.text}</Alert>}
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        When on, anyone with the link can view your public learning record and the badges you&apos;ve chosen to share.
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={publicPageShare}
                                disabled={publicSaving}
                                onChange={e => handleTogglePublic(e.target.checked)}
                            />
                        }
                        label={publicPageShare ? 'Public' : 'Private'}
                    />
                    {publicPageShare && (
                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                            <TextField
                                value={publicUrl}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                            <Button onClick={handleCopyUrl} variant="outlined" size="small" sx={{ whiteSpace: 'nowrap' }}>
                                {copied ? 'Copied' : 'Copy link'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Change Password Section */}
            <Card sx={{ mb: 3 }}>
                <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon color="action" fontSize="small" />
                    <Typography fontWeight={600}>Change Password</Typography>
                </Box>
                <CardContent>
                    {passwordMsg && <Alert severity={passwordMsg.type} sx={{ mb: 2 }}>{passwordMsg.text}</Alert>}
                    <Box component="form" onSubmit={handlePasswordSubmit} display="flex" flexDirection="column" gap={2}>
                        <TextField
                            id="currentPassword"
                            label="Current Password"
                            type="password"
                            value={currentPassword}
                            onChange={e => setCurrentPassword(e.target.value)}
                            required
                            size="small"
                            fullWidth
                        />
                        <TextField
                            id="newPassword"
                            label="New Password"
                            type="password"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            required
                            inputProps={{ minLength: 8 }}
                            size="small"
                            fullWidth
                        />
                        <TextField
                            id="confirmPassword"
                            label="Confirm Password"
                            type="password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                            inputProps={{ minLength: 8 }}
                            size="small"
                            fullWidth
                        />
                        <Box>
                            <Button type="submit" variant="contained" disabled={passwordSaving}>
                                {passwordSaving ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                                Change Password
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
}
